import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { catchError, of } from 'rxjs';
import { ConfigService } from '../../shared-services/config.service';
import { SpricedFilterDialogComponent } from '../spriced-filter-dialog/spriced-filter-dialog.component';

export interface SpricedSavedFilter {
  [key: string]: any;
  name: string;
  description?: string;
  updatedBy?: string;
  lastUpdatedOn?: string;
  payload?: any;
  raw?: any;
}

interface SavedFilterTableColumn {
  key: string;
  label: string;
  sortable?: boolean;
}

@Component({
  selector: 'app-spriced-saved-filters-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatSelectModule,
  ],
  templateUrl: './spriced-saved-filters-dialog.component.html',
  styleUrls: ['./spriced-saved-filters-dialog.component.scss'],
})
export class SpricedSavedFiltersDialogComponent implements OnInit {
  readonly pageSize = 15;
  resolvedTableColumns: SavedFilterTableColumn[] = [];
  filters: SpricedSavedFilter[] = [];
  selectedFilter: SpricedSavedFilter | null = null;
  selectedName = '';
  currentPage = 1;
  sortColumn: keyof SpricedSavedFilter | null = null;
  sortDirection: 'asc' | 'desc' = 'asc';
  loading = false;
  errorMessage = '';
  editModalOpen = false;
  editName = '';
  editDescription = '';
  editSaving = false;
  savedFilterFilterPayload: any = null;
  hasSavedFilterFilterApplied = false;

  constructor(
    private dialogRef: MatDialogRef<SpricedSavedFiltersDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private configService: ConfigService,
    private dialog: MatDialog,
  ) {
  }

  ngOnInit(): void {
    this.resolvedTableColumns = this.resolveTableColumns();
    this.loadSavedFilters();
  }

  get dialogTitle(): string {
    return String(this.data?.action?.dialogTitle || this.data?.action?.title || 'Saved filters');
  }

  get tableTitle(): string {
    return String(this.data?.action?.tableTitle || 'Filter list');
  }

  get tableColumns(): SavedFilterTableColumn[] {
    return this.resolvedTableColumns;
  }

  private resolveTableColumns(): SavedFilterTableColumn[] {
    const configuredColumns = this.data?.action?.tableColumns || this.data?.tableColumns;
    if (Array.isArray(configuredColumns) && configuredColumns.length) {
      return configuredColumns
        .map((column: any) => ({
          key: String(column?.key || '').trim(),
          label: String(column?.label || column?.key || '').trim(),
          sortable: column?.sortable !== false,
        }))
        .filter((column: SavedFilterTableColumn) => !!column.key);
    }

    return this.getSavedFilterTableColumns();
  }

  get selectionFieldKey(): string {
    return String(this.data?.action?.selectionFieldKey || 'name');
  }

  get showNameFilter(): boolean {
    return this.data?.action?.showNameFilter !== false;
  }

  get showEditAction(): boolean {
    return this.data?.action?.showEditAction !== false;
  }

  get showFilterAction(): boolean {
    return this.data?.action?.showFilterAction !== false;
  }

  get showClearFilterAction(): boolean {
    return this.showFilterAction;
  }

  get showFooterActions(): boolean {
    return this.data?.action?.showFooterActions !== false;
  }

  get applyButtonLabel(): string {
    return String(this.data?.action?.applyButtonLabel || 'Apply');
  }

  get emptyStateMessage(): string {
    return String(this.data?.action?.emptyStateMessage || 'No saved filters found.');
  }

  get loadingMessage(): string {
    return String(this.data?.action?.loadingMessage || 'Loading saved filters...');
  }

  get errorStateMessage(): string {
    return this.errorMessage || String(this.data?.action?.errorMessage || 'Unable to load saved filters.');
  }

  get filteredRows(): SpricedSavedFilter[] {
    const selectedName = this.showNameFilter ? String(this.selectedName || '').trim() : '';
    const selectionKey = this.selectionFieldKey;
    let rows = selectedName
      ? this.filters.filter((filter) => String(filter?.[selectionKey] || '') === selectedName)
      : [...this.filters];

    if (this.sortColumn) {
      const sortColumn = this.sortColumn;
      const direction = this.sortDirection === 'asc' ? 1 : -1;
      rows = rows.sort((left, right) =>
        String(left?.[sortColumn] || '').localeCompare(String(right?.[sortColumn] || ''), undefined, {
          numeric: true,
          sensitivity: 'base',
        }) * direction
      );
    }

    return rows;
  }

  get pagedRows(): SpricedSavedFilter[] {
    const startIndex = (this.currentPage - 1) * this.pageSize;
    return this.filteredRows.slice(startIndex, startIndex + this.pageSize);
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.filteredRows.length / this.pageSize));
  }

  get pageNumbers(): number[] {
    return Array.from({ length: Math.min(this.totalPages, 5) }, (_, index) => index + 1);
  }

  selectName(name: string): void {
    this.selectedName = name;
    this.currentPage = 1;
    this.selectedFilter = this.filteredRows[0] || null;
  }

  selectRow(filter: SpricedSavedFilter): void {
    this.selectedFilter = filter;
  }

  sortBy(column: keyof SpricedSavedFilter): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
  }

  sortColumnByKey(column: SavedFilterTableColumn): void {
    if (column?.sortable === false || !column?.key) {
      return;
    }

    this.sortBy(column.key);
  }

  trackByColumnKey(_index: number, column: SavedFilterTableColumn): string {
    return column.key;
  }

  goToPage(page: number): void {
    this.currentPage = Math.min(Math.max(page, 1), this.totalPages);
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  apply(): void {
    this.dialogRef.close(this.selectedFilter);
  }

  openEditModal(): void {
    if (!this.selectedFilter) {
      return;
    }

    this.editName = this.selectedFilter.name || '';
    this.editDescription = this.selectedFilter.description || '';
    this.editModalOpen = true;
  }

  closeEditModal(): void {
    if (this.editSaving) {
      return;
    }

    this.editModalOpen = false;
  }

  saveEdit(): void {
    const action = this.data?.action || {};
    const apiUrl = action?.editFilterApiUrl;
    if (!apiUrl || !this.selectedFilter) {
      this.editModalOpen = false;
      return;
    }

    this.editSaving = true;
    const method = String(action?.editFilterMethod || 'post').toLowerCase() === 'get' ? 'get' : 'post';
    const payload = this.buildEditPayload(action);
    const requestConfig = {
      ...action,
      customPayload: payload,
      rawPayload: payload,
      skipDefaultPayload: true,
      functionName: action?.editFilterFunctionName || action?.updateFilterFunctionName || action?.functionName || 'GenericSavedFiltersUpdateService',
    };

    this.configService.getApi(apiUrl, { requestParams: {} }, requestConfig, method).pipe(
      catchError((error) => {
        console.error('Error editing saved filter:', error);
        this.errorMessage = 'Unable to edit saved filter.';
        return of(null);
      }),
    ).subscribe(() => {
      this.editSaving = false;
      this.editModalOpen = false;
      this.loadSavedFilters(this.editName);
    });
  }

  openSavedFiltersFilterDialog(): void {
    const action = this.data?.action || {};
    const dialogRef = this.dialog.open(SpricedFilterDialogComponent, {
      width: action?.filterDialogWidth || '60vw',
      maxWidth: action?.filterDialogMaxWidth || '70vw',
      panelClass: 'spriced-filter-dialog-panel',
      data: {
        filterPayload: {
          attributesList: this.tableColumns.map((column) => column.key),
          requestData: {
            entity: action?.entity || 'saved_filter',
          },
        },
        appliedFilterPayload: this.savedFilterFilterPayload,
        selectedFilters: this.data?.currentFilters || {},
        tableColumns: this.tableColumns,
        defaultApplyPayload: action?.customPayload || action?.payload || null,
        submitFunctionName: action?.functionName || action?.savedFiltersFunctionName || 'GenericSavedFiltersService',
        entity: action?.entity || 'saved_filter',
        workflowId: action?.workflowId || 'UI',
        version: action?.version || '1.0',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }

      this.savedFilterFilterPayload = result;
      this.hasSavedFilterFilterApplied = true;
      this.loadSavedFilters();
    });
  }

  clearSavedFiltersFilter(): void {
    if (!this.hasSavedFilterFilterApplied) {
      return;
    }

    this.savedFilterFilterPayload = null;
    this.hasSavedFilterFilterApplied = false;
    this.loadSavedFilters();
  }

  private loadSavedFilters(preferredName?: string): void {
    const action = this.data?.action || {};
    const apiUrl = action?.apiUrl || action?.savedFiltersApiUrl;

    if (!apiUrl) {
      this.setFilters(this.resolveFilters(this.data));
      return;
    }

    this.loading = true;
    this.errorMessage = '';

    const method = String(action?.savedFiltersMethod || action?.method || 'post').toLowerCase() === 'get' ? 'get' : 'post';
    const payload = this.savedFilterFilterPayload || action?.customPayload || action?.payload || {};
    const requestConfig = {
      ...action,
      customPayload: payload,
      rawPayload: payload,
      functionName: action?.functionName || action?.savedFiltersFunctionName || 'GenericSavedFiltersService',
      skipDefaultPayload: true,
    };

    this.configService.getApi(apiUrl, { requestParams: {} }, requestConfig, method).pipe(
      catchError((error) => {
        console.error('Error loading saved filters:', error);
        this.errorMessage = 'Unable to load saved filters.';
        return of(null);
      }),
    ).subscribe((response) => {
      this.loading = false;
      const rows = this.extractFiltersFromResponse(response);
      this.setFilters(rows, preferredName);
    });
  }

  private setFilters(filters: SpricedSavedFilter[], preferredName?: string): void {
    this.filters = filters.map((filter: any) => this.normalizeFilter(filter));
    this.currentPage = 1;
    const selectionKey = this.selectionFieldKey;
    this.selectedFilter = this.filters.find((filter) => String(filter?.[selectionKey] || '') === preferredName) || this.filters[0] || null;
    this.selectedName = '';
  }

  private resolveFilters(data: any): SpricedSavedFilter[] {
    const configuredFilters =
      data?.savedFilters ||
      data?.action?.savedFilters ||
      data?.action?.filters ||
      data?.filters;

    if (Array.isArray(configuredFilters) && configuredFilters.length) {
      return configuredFilters.map((filter: any) => this.normalizeFilter(filter));
    }

    return [];
  }

  private extractFiltersFromResponse(response: any): any[] {
    if (Array.isArray(response)) {
      return response;
    }

    const candidates = [
      response?.savedFilters,
      response?.filters,
      response?.filterList,
      response?.data,
      response?.rows,
      response?.responseList?.[0]?.data?.[0]?.savedFilters,
      response?.responseList?.[0]?.data?.[0]?.filters,
      response?.responseList?.[0]?.data,
    ];

    for (const candidate of candidates) {
      if (Array.isArray(candidate)) {
        return candidate;
      }
    }

    return [];
  }

  private normalizeFilter(filter: any): SpricedSavedFilter {
    const normalized: SpricedSavedFilter = {
      ...(filter || {}),
      raw: filter,
    };

    for (const column of this.tableColumns) {
      if (normalized[column.key] === undefined && filter?.raw?.[column.key] !== undefined) {
        normalized[column.key] = filter.raw[column.key];
      }
    }

    normalized.name = String(normalized?.name || filter?.filterName || filter?.label || filter?.entity || '');
    normalized.description = String(normalized?.description || filter?.desc || filter?.uploadStatus || '');
    normalized.updatedBy = String(normalized?.updatedBy || filter?.updated_by || filter?.createdBy || filter?.uploadedBy || '');
    normalized.lastUpdatedOn = String(normalized?.lastUpdatedOn || filter?.last_updated_on || filter?.updatedOn || filter?.uploadedDate || '');
    normalized.payload = filter?.payload || filter?.requestData || filter;

    return normalized;
  }

  private buildEditPayload(action: any): any {
    const selected = this.selectedFilter;
    const raw = selected?.raw || {};
    return {
      requestData: {
        entity: action?.entity || 'saved_filter',
        filterId: raw?.id || raw?.filterId || raw?.uuid || raw?.filter_id || selected?.name,
        name: this.editName,
        description: this.editDescription,
        originalName: selected?.name,
        payload: selected?.payload,
      },
      responseList: [],
      entity: action?.entity || 'saved_filter',
      workflowId: action?.workflowId || 'UI',
      endpoints: [
        {
          functionName: action?.editFilterFunctionName || action?.updateFilterFunctionName || 'GenericSavedFiltersUpdateService',
          version: action?.version || '1.0',
        },
      ],
      communicationType: action?.communicationType || 'rest',
      version: action?.version || '1.0',
      functionName: action?.editFilterFunctionName || action?.updateFilterFunctionName || 'GenericSavedFiltersUpdateService',
    };
  }

  private getSavedFilterTableColumns(): Array<{ key: string; label: string; sortable: boolean }> {
    return [
      { key: 'name', label: 'Name', sortable: true },
      { key: 'description', label: 'Description', sortable: true },
      { key: 'updatedBy', label: 'Updated By', sortable: true },
      { key: 'lastUpdatedOn', label: 'Last Updated On', sortable: true },
    ];
  }
}
